import { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, Users, X } from 'lucide-react';
import clsx from 'clsx';
import type { Team } from '../types';

interface TeamMultiSelectProps {
  teams: Team[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  /** Compact inline mode — chips + small add button, no border box */
  inline?: boolean;
}

export default function TeamMultiSelect({
  teams,
  selectedIds,
  onChange,
  disabled = false,
  placeholder = 'Выберите команды...',
  label,
  inline = false,
}: TeamMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus search when opened
  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  const toggle = (id: string) => {
    if (disabled) return;
    const next = selectedIds.includes(id)
      ? selectedIds.filter(x => x !== id)
      : [...selectedIds, id];
    onChange(next);
  };

  const remove = (id: string) => {
    if (disabled) return;
    onChange(selectedIds.filter(x => x !== id));
  };

  const selectedTeams = teams.filter(t => selectedIds.includes(t.id));
  const filtered = teams.filter(
    t => !search.trim() || t.name.toLowerCase().includes(search.trim().toLowerCase())
  );

  // ─── Inline mode (for SeriesDetailPage) ────────────────────────────────
  if (inline) {
    const available = filtered.filter(t => !selectedIds.includes(t.id));
    return (
      <div ref={containerRef} className="relative inline-flex items-center gap-1.5 flex-wrap">
        <Users className="h-4 w-4 text-gray-400 flex-shrink-0" />
        {selectedTeams.length > 0 ? (
          selectedTeams.map(t => (
            <span
              key={t.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 transition-all"
            >
              {t.name}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => remove(t.id)}
                  className="ml-0.5 hover:text-red-500 transition-colors"
                  title={`Убрать ${t.name}`}
                >
                  &times;
                </button>
              )}
            </span>
          ))
        ) : (
          <span className="text-sm text-gray-400">Без команды</span>
        )}
        {!disabled && available.length > 0 && (
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-medium border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-primary-400 hover:text-primary-600 dark:hover:border-primary-500 dark:hover:text-primary-400 transition-all"
          >
            + Команда
          </button>
        )}
        {isOpen && available.length > 0 && (
          <div className="absolute top-full left-0 mt-1 z-50 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden animate-in fade-in slide-in-from-top-1">
            <div className="p-2 border-b border-gray-100 dark:border-gray-700">
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Поиск..."
                className="w-full px-2 py-1 text-xs border rounded bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <div className="max-h-48 overflow-y-auto py-1">
              {available.length > 0 ? (
                available.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      toggle(t.id);
                      if (available.length <= 1) {
                        setIsOpen(false);
                        setSearch('');
                      }
                    }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                  >
                    <Users className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                    {t.name}
                  </button>
                ))
              ) : (
                <div className="px-3 py-2 text-xs text-gray-400">Не найдено</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── Standard mode (for modals) ────────────────────────────────────────
  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label}
        </label>
      )}

      {/* Trigger */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={clsx(
          'w-full min-h-[42px] px-3 py-2 flex items-center flex-wrap gap-1.5',
          'border rounded-lg shadow-sm transition-all text-left',
          'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600',
          'hover:border-primary-400 dark:hover:border-primary-500',
          isOpen && 'ring-2 ring-primary-500 border-primary-500',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        {selectedTeams.length > 0 ? (
          <>
            {selectedTeams.map(t => (
              <span
                key={t.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
              >
                {t.name}
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(t.id);
                    }}
                    className="hover:text-red-500 transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))}
          </>
        ) : (
          <span className="text-sm text-gray-400 dark:text-gray-500">{placeholder}</span>
        )}
        <ChevronDown
          className={clsx(
            'h-4 w-4 ml-auto text-gray-400 transition-transform flex-shrink-0',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-gray-100 dark:border-gray-700">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Поиск команд..."
              className="w-full px-3 py-1.5 text-sm border rounded-md bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          {/* Options */}
          <div className="max-h-52 overflow-y-auto py-1">
            {filtered.length > 0 ? (
              filtered.map(t => {
                const isSelected = selectedIds.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => toggle(t.id)}
                    className={clsx(
                      'w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors',
                      isSelected
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50',
                    )}
                  >
                    <div
                      className={clsx(
                        'h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 transition-all',
                        isSelected
                          ? 'bg-primary-600 border-primary-600'
                          : 'border-gray-300 dark:border-gray-500',
                      )}
                    >
                      {isSelected && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <Users className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{t.name}</span>
                    {t.my_role && (
                      <span className="ml-auto text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                        {t.my_role === 'owner' ? 'Владелец' : 'Участник'}
                      </span>
                    )}
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-4 text-center text-sm text-gray-400 dark:text-gray-500">
                {search.trim() ? 'Команды не найдены' : 'Нет доступных команд'}
              </div>
            )}
          </div>

          {/* Footer hint */}
          {selectedTeams.length > 0 && (
            <div className="px-3 py-1.5 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-400 dark:text-gray-500">
              Выбрано: {selectedTeams.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
